/**
 * HeroSection — cinematic top-of-fold panel (design lock Feb 2026 · rev 2).
 *
 * Anatomy:
 *   • Full-bleed cinematic archive-room background image (Nano Banana AI-generated)
 *   • Dark cinematic gradient overlay for legibility
 *   • Redaction/grid overlays (subtle, decorative)
 *   • Display headline with red accent line + service tag
 *   • Mission sub-lead
 *   • Dual CTAs (Enter / Join)
 *   • Trust-signals band along the bottom (5 icons, replaces old stat strip)
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, ScrollText, Eye, Scale, Map as MapIcon,
} from "lucide-react";
import { EnvironmentalSeal } from "../EnvironmentalSeal";
import { EnvironmentalRoom } from "../EnvironmentalRoom";
import { AnimatedStat } from "../AnimatedStat";
import { useArchiveStats } from "../../hooks/useArchiveStats";

const TRUST_SIGNALS = [
  { icon: ShieldCheck, label: "Evidence First",       hint: "Every claim traceable" },
  { icon: Scale,       label: "Editorial Review",     hint: "Owner-audited" },
  { icon: Eye,         label: "Transparency",         hint: "Every source dated" },
  { icon: ScrollText,  label: "Source-Backed",        hint: "Verified references" },
  { icon: MapIcon,     label: "Evidence Map",         hint: "Cross-referenced" },
];

const HeroSection = () => {
  const stats = useArchiveStats();
  return (
    <section
      data-testid="home-hero"
      className="relative overflow-hidden isolate"
      style={{ background: "#0B0A0D" }}
    >
      {/* Environmental room — Reception / Intelligence Desk. The entrance
          to the facility; subtle, static, decorative (layers 2–4 of the
          locked stack). The red spill below stays independent (layer 5). */}
      <EnvironmentalRoom room="reception" />
      {/* Cinematic gradient — ensures headline legibility over any hero image */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10"
        style={{
          background: `
            radial-gradient(ellipse 100% 60% at 50% 0%, rgba(217,45,32,0.18) 0%, transparent 60%),
            radial-gradient(ellipse 70% 50% at 20% 80%, rgba(80,50,20,0.20) 0%, transparent 55%),
            linear-gradient(180deg, rgba(11,10,13,0.55) 0%, rgba(11,10,13,0.72) 45%, rgba(11,10,13,0.94) 90%, #0B0A0D 100%)
          `,
        }}
      />
      {/* Environmental seal — see EnvironmentalSeal.jsx for locked
          branding constants. Intensity "hero" is the 100 % baseline. */}
      <EnvironmentalSeal intensity="hero" />

      {/* Grid overlay — evokes tactical / archive UI */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(228,223,211,1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(228,223,211,1) 1px, transparent 1px)
          `,
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(ellipse 80% 100% at 50% 50%, black 30%, transparent 90%)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 100% at 50% 50%, black 30%, transparent 90%)",
        }}
      />

      {/* Redaction-style horizontal bars — decorative */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute right-[6%] top-[12%] h-3 w-40 md:w-60 opacity-25" style={{ background: "#3A3630" }} />
        <div className="absolute right-[18%] top-[20%] h-2 w-24 md:w-40 opacity-20" style={{ background: "#3A3630" }} />
        <div className="absolute right-[8%] top-[27%] h-2 w-16 md:w-32 opacity-20" style={{ background: "#3A3630" }} />
        <div className="absolute left-[10%] bottom-[15%] h-1 w-28 opacity-20" style={{ background: "#3A3630" }} />
      </div>

      {/* Bottom fade for the operations pulse to bleed into */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-32 pointer-events-none"
        style={{ background: "linear-gradient(180deg, transparent 0%, rgba(11,10,13,0.98) 100%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-16 md:pt-36 pb-12 md:pb-28">
        {/* Service tag — locked branding: pure typography only. The
            environmental Archive seal in the background carries every
            piece of brand weight; the site header carries the primary
            logo. No mini-logo or shield in this label per the locked
            branding direction. */}
        <div className="mb-5 md:mb-8" data-testid="home-hero-service-tag">
          <span className="font-mono text-[10px] uppercase tracking-[0.5em] text-[#A39E93]">
            The Archive · Independent Intelligence Bureau
          </span>
        </div>

        {/* Headline — cinematic, two-line lockup with a subtle blinking
            typewriter cursor at the end (design lock: red `_`). */}
        <h1
          data-testid="home-hero-title"
          className="font-display text-[#E4DFD3] leading-[0.94] tracking-tight max-w-5xl
                     text-[44px] sm:text-[56px] md:text-[80px] lg:text-[96px]"
        >
          The internet&apos;s most<br />
          <span style={{ color: "#D92D20" }} className="cursor-blink" data-testid="home-hero-title-cursor">controversial dossiers.</span>
        </h1>

        <p
          data-testid="home-hero-lead"
          className="mt-5 md:mt-8 max-w-2xl text-[14px] md:text-[17px] leading-relaxed text-[#B9B2A4]"
        >
          Source-backed investigations across intelligence, finance, military, and modern
          mysteries. Every claim traceable. Every document dated. Every source audited.
          Read what mainstream archives redact.
        </p>

        {/* Dual CTAs */}
        <div className="mt-7 md:mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
          <Link
            to="/archive"
            data-testid="home-hero-cta-primary"
            className="inline-flex items-center gap-2 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.35em]
                       border-2 border-[#D92D20] text-[#E4DFD3] hover:bg-[#D92D20] transition-colors"
            style={{ background: "rgba(217,45,32,0.22)" }}
          >
            Enter the Archive <ArrowRight size={13} />
          </Link>
          <Link
            to="/auth/signup"
            data-testid="home-hero-cta-secondary"
            className="inline-flex items-center gap-2 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.35em]
                       border border-[#454038] text-[#A39E93] hover:border-[#A39E93] hover:text-[#E4DFD3] transition-colors"
            style={{ background: "rgba(11,10,13,0.55)" }}
          >
            Join The Archive
          </Link>
        </div>

        {/* Stat strip — live archive statistics (single source of truth) */}
        <div className="mt-10 md:mt-20 grid grid-cols-3 gap-3 md:gap-8 max-w-3xl">
          <StatBox label="Dossiers Archived"
                   value={<AnimatedStat value={stats?.published_dossiers ?? null} testid="hero-stat-dossiers" />} />
          <StatBox label="Verified Sources"
                   value={<AnimatedStat value={stats?.documents ?? null} suffix="+" testid="hero-stat-sources" />} />
          <StatBox label="Documented Links"
                   value={<AnimatedStat value={stats?.relationships ?? null} testid="hero-stat-relationships" />} />
        </div>
      </div>

      {/* Trust-signals band — replaces flat "value proposition" bar from mockup */}
      <div
        data-testid="home-hero-trust-band"
        className="relative border-t"
        style={{ borderColor: "#2D2A26", background: "rgba(11,10,13,0.92)", backdropFilter: "blur(8px)" }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 md:py-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-3 gap-y-2.5 md:gap-6">
          {TRUST_SIGNALS.map(({ icon: Icon, label, hint }) => (
            <div key={label} className="flex items-start gap-2.5 md:gap-3 min-w-0">
              <div
                className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center border shrink-0"
                style={{ borderColor: "#3A3630", background: "rgba(217,45,32,0.08)" }}
              >
                <Icon size={13} className="text-[#D92D20]" />
              </div>
              <div className="min-w-0 flex-1 leading-snug">
                <div className="font-mono text-[9px] md:text-[10px] uppercase tracking-[0.18em] md:tracking-[0.22em] text-[#E4DFD3] break-words">{label}</div>
                <div className="font-mono text-[8px] md:text-[9px] uppercase tracking-[0.14em] md:tracking-[0.18em] text-[#7B7168] break-words mt-0.5">{hint}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const StatBox = ({ label, value }) => (
  <div className="border-l-2 pl-3 md:pl-4" style={{ borderColor: "#D92D20" }}>
    <div className="font-display text-[22px] md:text-[28px] text-[#E4DFD3] leading-none">{value}</div>
    <div className="font-mono text-[9px] uppercase tracking-[0.35em] text-[#8A8377] mt-1.5">{label}</div>
  </div>
);

export default HeroSection;
